import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Hide initial loader when component mounts
  useEffect(() => {
    const loader = document.getElementById('initial-loader');
    if (loader) {
      loader.style.display = 'none';
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth`, { password });
      if (response.data.success) {
        // Store auth in session
        sessionStorage.setItem('smartshift_auth', 'true');
        onLogin();
      }
    } catch (err) {
      setError('Invalid password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#080c12] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background dot pattern */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />
      
      {/* Subtle glow effect at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/5 blur-[100px] rounded-full" />
      
      <div className="relative w-full max-w-[400px] mx-2 sm:mx-4">
        {/* Corner accents */}
        <div className="absolute -top-[2px] -left-[2px] w-3 sm:w-4 h-3 sm:h-4 border-t-2 border-l-2 border-cyan-500/50" />
        <div className="absolute -top-[2px] -right-[2px] w-3 sm:w-4 h-3 sm:h-4 border-t-2 border-r-2 border-cyan-500/50" />
        <div className="absolute -bottom-[2px] -left-[2px] w-3 sm:w-4 h-3 sm:h-4 border-b-2 border-l-2 border-cyan-500/50" />
        <div className="absolute -bottom-[2px] -right-[2px] w-3 sm:w-4 h-3 sm:h-4 border-b-2 border-r-2 border-cyan-500/50" />
        
        {/* Main card */}
        <div className="bg-[#0a1018]/95 border border-gray-800 backdrop-blur-sm p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#1a2535] rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#d4a636]" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-[0.1em] text-[#d4a636] font-mono uppercase mb-2">
              SMARTSHIFT
            </h1>
            <p className="text-xs tracking-[0.15em] text-gray-500 font-mono uppercase">
              SECURE ACCESS
            </p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-gray-400" />
                <span className="text-xs tracking-[0.2em] text-gray-400 font-mono uppercase">PASSWORD</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="login-password-input"
                  className="w-full px-4 py-3 pr-12 bg-[#0d1520] border border-gray-700 rounded text-white font-mono placeholder-gray-500 focus:outline-none focus:border-[#d4a636] transition-colors"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-red-400 text-sm font-mono">{error}</span>
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !password}
              data-testid="login-submit-button"
              className="w-full px-4 py-3 bg-[#d4a636] hover:bg-[#c49526] active:bg-[#b38520] disabled:bg-gray-600 disabled:cursor-not-allowed text-[#0a1018] font-mono font-semibold rounded flex items-center justify-center gap-2 transition-colors touch-manipulation"
            >
              {loading ? (
                <span>Authenticating...</span>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Access App</span>
                </>
              )}
            </button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-gray-800">
            <p className="text-center text-[10px] sm:text-xs tracking-[0.1em] text-gray-600 font-mono uppercase">
              ENCRYPTED & SECURE CONNECTION
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
